import { useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import Select from "react-select";
import { DROPDOWN_STALE_TIME } from "utils/constants";
import { Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import { fetchEmployees } from "services/dropdown";
import zoneLocalStorageId from "utils/zone";

const MarketingPersonDropdown = ({
  errors,
  control,
  required,
  defaultValue,
  setValue,
  onMembershipChange,
  employeeType = "MARKETING",
  loggedInEmployeeCode,
}) => {
  const login = useSelector((state) => state.jwtAuthreducer);

  const authEmployeeCodeId =
    loggedInEmployeeCode ||
    login?.user?.employee_code_id ||
    (localStorage.getItem("employee_code_id")
      ? Number(localStorage.getItem("employee_code_id"))
      : null);

  const authEmployeeCode =
    login?.user?.employee_code ||
    localStorage.getItem("employee_code") ||
    "";

  const authEmployeeName =
    login?.user?.employee_name ||
    login?.user?.name ||
    login?.user?.username ||
    "";

  const {
    data: memberships,
    isLoading,
    isError,
  } = useQuery(
    ["fetch-employees", zoneLocalStorageId(), employeeType],
    () => fetchEmployees(zoneLocalStorageId(), employeeType),
    {
      staleTime: DROPDOWN_STALE_TIME,
    }
  );

  const options = useMemo(() => {
    const list =
      memberships?.data?.data?.map((item) => ({
        value: item?.employee_code_id,
        label: item?.employee_name,
        employee_code: item?.employee_code,
        employee_code_id: item?.employee_code_id,
      })) || [];

    if (authEmployeeCodeId) {
      const exists = list.some(
        (item) =>
          item.value === authEmployeeCodeId ||
          Number(item.value) === Number(authEmployeeCodeId) ||
          (authEmployeeCode &&
            String(item.employee_code) === String(authEmployeeCode))
      );
      if (!exists && authEmployeeName) {
        list.unshift({
          value: authEmployeeCodeId,
          label: authEmployeeName,
          employee_code: authEmployeeCode,
          employee_code_id: authEmployeeCodeId,
        });
      }
    }

    return list;
  }, [memberships, authEmployeeCodeId, authEmployeeCode, authEmployeeName]);

  const targetDefault = useMemo(() => {
    if (defaultValue && defaultValue !== 0 && defaultValue !== "0") {
      return defaultValue;
    }
    return authEmployeeCodeId || "";
  }, [defaultValue, authEmployeeCodeId]);

  useEffect(() => {
    if (options.length > 0 && setValue) {
      const selectedOption = options.find(
        (opt) =>
          opt.value === targetDefault ||
          Number(opt.value) === Number(targetDefault) ||
          String(opt.value) === String(targetDefault) ||
          (authEmployeeCode &&
            String(opt.employee_code) === String(authEmployeeCode)) ||
          (authEmployeeCodeId &&
            (opt.value === authEmployeeCodeId ||
              Number(opt.value) === Number(authEmployeeCodeId)))
      );

      if (selectedOption) {
        setValue("marketing_person_id", selectedOption.value, {
          shouldValidate: true,
          shouldDirty: true,
        });
        if (onMembershipChange) {
          onMembershipChange(selectedOption);
        }
      }
    }
  }, [
    options,
    targetDefault,
    setValue,
    authEmployeeCode,
    authEmployeeCodeId,
    onMembershipChange,
  ]);

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error loading memberships</div>;

  return (
    <Controller
      name="marketing_person_id"
      control={control}
      defaultValue={targetDefault || ""}
      rules={{ required: required ? "Marketing Person is required" : false }}
      render={({ field }) => (
        <Select
          {...field}
          options={options}
          isClearable
          placeholder="Select Marketing Person"
          value={
            options.find(
              (option) =>
                option.value === field.value ||
                (field.value && Number(option.value) === Number(field.value)) ||
                (field.value && String(option.value) === String(field.value))
            ) ||
            (targetDefault
              ? options.find(
                  (opt) =>
                    opt.value === targetDefault ||
                    Number(opt.value) === Number(targetDefault)
                )
              : null) ||
            null
          }
          onChange={(selectedOption) => {
            field.onChange(selectedOption?.value || "");

            if (selectedOption && setValue) {
              setValue("marketing_person_id", selectedOption.value);
            } else if (setValue) {
              setValue("marketing_person_id", "");
            }

            if (onMembershipChange) {
              onMembershipChange(selectedOption);
            }
          }}
        />
      )}
    />
  );
};

export default MarketingPersonDropdown;
